<template>
  <div class="stack vertical">
    <div
      v-for="(part, i) in form.parts"
      class="stack vertical gap-3 bg-gradient-to-b from-white/20 via-transparent to-gray-300/10 last:to-transparent border-b last:border-b-0 border-gray-200 px-5 @2xl:px-7 py-4"
    >
      <!-- TODO: Add aria-label that includes teh word "points" -->
      <s-label
        v-if="
          !part.pointsAvailable && !isGeneralFeedback(part) && !isBlockCriterionFeedbackShown(part)
        "
      >
        <s-latex v-if="part.label" :content="part.label" />
        <template v-else>{{ t('generalFeedback') }}</template>
      </s-label>
      <s-input-field
        v-else-if="part.pointsAvailable"
        :id="keyFor(part) + '_pointsEarned'"
        type="number"
        step="0.01"
        v-model="part.pointsEarned"
        :error="$page.props.errors[`parts.${i}.pointsEarned`]"
        :min="0"
        :max="part.pointsAvailable"
        inputClass="!rounded-r-none"
      >
        <template #label>
          <s-latex v-if="part.label" :content="part.label" />
          <template v-else>{{ t('generalFeedback') }}</template>
        </template>
        <template #suffix>
          <span class="text-gray-400 font-medium">/</span>
          <span>
            {{ t('xPoints', {count: part.pointsAvailable}, part.pointsAvailable) }}
          </span>
        </template>
      </s-input-field>
      <div v-if="!isBlockCriterionFeedbackShown(part)" class="-my-1">
        <s-btn-link type="button" @click.prevent="showBlockCriteriaFeedback(part)" icon="pencil">
          {{ isGeneralFeedback(part) ? t('addGeneralFeedback') : t('addFeedback') }}
        </s-btn-link>
      </div>
      <s-textarea-field
        v-else
        :id="keyFor(part) + '_feedback'"
        v-model="part.feedback[0]"
        :error="$page.props.errors[`parts.${i}.feedback`]"
      >
        <template #label>
          <span class="flex w-full gap-4 justify-between items-end">
            <span v-if="!part.pointsAvailable">
              {{ part.label }}
            </span>
            <span v-else>
              {{ isGeneralFeedback(part) ? t('generalFeedback') : t('feedback') }}
            </span>
            <s-btn-link
              v-if="isBlockCriterionFeedbackShown(part)"
              type="button"
              @click.prevent="removeBlockCriteriaFeedback(part)"
              icon="delete"
            >
              {{ t('deleteFeedback') }}
            </s-btn-link>
          </span>
        </template>
      </s-textarea-field>
    </div>
  </div>
</template>

<script setup lang="ts">
import ManualGradingSubmissionDto = App.DTOs.AssignmentMarking.ManualGradingSubmissionDto;
import SInputField from '../../../design-system/SInputField.vue';
import STextareaField from '../../../design-system/STextareaField.vue';
import {useI18n} from 'vue-i18n';
import {InertiaForm} from '@inertiajs/vue3';
import SLatex from '../../../components/SLatex.vue';
import SBtnLink from '../../../design-system/SBtnLink.vue';
import ManualGradingPartDto = App.DTOs.AssignmentMarking.ManualGradingPartDto;
import SLabel from '../../../design-system/SLabel.vue';

const {t} = useI18n();
const shownFeedbacks: Record<string, boolean> = {};

const props = defineProps<{
  form: InertiaForm<ManualGradingSubmissionDto>;
}>();

function keyFor(part: ManualGradingPartDto): string {
  return `${part.blockId}_${part.criterionName}`;
}

function isBlockCriterionFeedbackShown(part: ManualGradingPartDto): boolean {
  return !!part.feedback.length || shownFeedbacks[keyFor(part)];
}

function showBlockCriteriaFeedback(part: ManualGradingPartDto) {
  part.feedback.push('');
}

function removeBlockCriteriaFeedback(part: ManualGradingPartDto) {
  part.feedback = [];
  shownFeedbacks[keyFor(part)] = false;
}

function isGeneralFeedback(part: ManualGradingPartDto): boolean {
  return !part.blockId && !part.criterionName;
}
</script>

<i18n>
{
  "en": {
    "addFeedback": "Add Feedback",
    "deleteFeedback": "Delete Feedback",
    "addGeneralFeedback": "Add General Feedback"
  },
  "fr": {
    "addFeedback": "Ajouter un commentaire",
    "deleteFeedback": "Retirer les Commentaires",
    "addGeneralFeedback": "Ajouter un commentaire général"
  }
}
</i18n>
